import { supabase } from './supabase';

/**
 * Manual Twilio activation, shared by the setup wizard (and reusable
 * elsewhere). Saves the business's own credentials, verifies the phone number
 * exists in their Twilio account, wires up the SMS/voice webhooks + TwiML app,
 * and activates the integration — the same steps as the Integrations modal's
 * "Activate Integration" button.
 */

function formatToE164(phone: string): string {
  let cleaned = phone.replace(/\D/g, '');
  if (!cleaned.startsWith('1') && cleaned.length === 10) cleaned = '1' + cleaned;
  return phone.trim().startsWith('+') ? phone.trim() : '+' + cleaned;
}

const WEBHOOK_BASE = 'https://idvgoctfaarxiryxprpy.supabase.co/functions/v1';

async function configureWebhooks(accountSid: string, authToken: string, phoneNumber: string): Promise<{ applicationSid: string | null }> {
  const credentials = btoa(`${accountSid}:${authToken}`);
  const formattedPhone = formatToE164(phoneNumber);

  // Resolve the number's SID (also proves the number belongs to this account).
  const listResp = await fetch(
    `https://api.twilio.com/2010-04-01/Accounts/${accountSid}/IncomingPhoneNumbers.json?PhoneNumber=${encodeURIComponent(formattedPhone)}`,
    { headers: { Authorization: `Basic ${credentials}` } },
  );
  if (!listResp.ok) {
    const err = await listResp.json().catch(() => null);
    throw new Error(err?.message || 'Could not verify your Twilio credentials — double-check the Account SID and Auth Token.');
  }
  const listData = await listResp.json();
  const numberRow = listData.incoming_phone_numbers?.[0];
  if (!numberRow) {
    throw new Error(`${formattedPhone} isn't a phone number in this Twilio account. Buy a number in the Twilio console first, then enter it here.`);
  }

  // Point the number's SMS/voice webhooks at Nexus.
  const updateResp = await fetch(
    `https://api.twilio.com/2010-04-01/Accounts/${accountSid}/IncomingPhoneNumbers/${numberRow.sid}.json`,
    {
      method: 'POST',
      headers: { Authorization: `Basic ${credentials}`, 'Content-Type': 'application/x-www-form-urlencoded' },
      body: new URLSearchParams({
        VoiceUrl: `${WEBHOOK_BASE}/twilio-call-webhook`,
        VoiceMethod: 'POST',
        StatusCallback: `${WEBHOOK_BASE}/twilio-call-status`,
        StatusCallbackMethod: 'POST',
        SmsUrl: `${WEBHOOK_BASE}/sms-webhook`,
        SmsMethod: 'POST',
      }),
    },
  );
  if (!updateResp.ok) {
    const err = await updateResp.json().catch(() => null);
    throw new Error(err?.message || 'Could not configure the number for texting.');
  }

  // TwiML app for the in-app Voice SDK (best-effort).
  let applicationSid: string | null = null;
  try {
    const appResp = await fetch(`https://api.twilio.com/2010-04-01/Accounts/${accountSid}/Applications.json`, {
      method: 'POST',
      headers: { Authorization: `Basic ${credentials}`, 'Content-Type': 'application/x-www-form-urlencoded' },
      body: new URLSearchParams({
        FriendlyName: 'Voice SDK Application (Auto-created)',
        VoiceUrl: `${WEBHOOK_BASE}/twilio-voice-handler`,
        VoiceMethod: 'POST',
        StatusCallback: `${WEBHOOK_BASE}/twilio-call-status`,
        StatusCallbackMethod: 'POST',
      }),
    });
    if (appResp.ok) applicationSid = (await appResp.json()).sid;
  } catch { /* voice SDK optional */ }

  return { applicationSid };
}

export async function activateManualTwilio(params: {
  businessId: string;
  accountSid: string;
  authToken: string;
  phoneNumber: string;
}): Promise<{ phoneNumber: string }> {
  if (!supabase) throw new Error('Supabase client not initialized');
  const accountSid = params.accountSid.trim();
  const authToken = params.authToken.trim();
  const phoneNumber = formatToE164(params.phoneNumber);

  if (!/^AC[a-zA-Z0-9]{32}$/.test(accountSid)) {
    throw new Error('That Account SID doesn\'t look right — it starts with "AC" and is 34 characters long.');
  }
  if (authToken.length < 30) {
    throw new Error('That Auth Token looks too short — copy it from the Twilio console home page.');
  }
  if (phoneNumber.replace(/\D/g, '').length < 11) {
    throw new Error('Enter the full phone number you bought in Twilio (e.g. +16045551234).');
  }

  // Verify + wire up webhooks BEFORE flipping the integration on.
  const { applicationSid } = await configureWebhooks(accountSid, authToken, phoneNumber);

  const { error } = await supabase
    .from('businesses')
    .update({
      twilio_account_sid: accountSid,
      twilio_auth_token: authToken,
      VITE_TWILIO_PHONE_NUMBER: phoneNumber,
      twilio_connection_method: 'manual',
      twilio_enabled: true,
      twilio_status: 'active',
      twilio_webhooks_configured: true,
      twilio_webhook_configured_at: new Date().toISOString(),
      ...(applicationSid ? { twilio_application_sid: applicationSid } : {}),
    })
    .eq('id', params.businessId);
  if (error) throw new Error(`Twilio is set up but saving failed: ${error.message}`);

  return { phoneNumber };
}
